import { decode, encode } from "@/fx/base62";
import { ARE_EQUAL, DateTime, IS } from "@/fx/data-helpers";
import assert from "assert";

// export const GLOBAL_FILE_INFO_IDENTIFIER_PREFIX = "_GLOBAL_/";

// export const GLOBAL_FILE_INFO_IDENTIFIER_REGEX = /^_GLOBAL_\/[0-9a-zA-Z]+$/;

export const FILE_INFO_IDENTIFIER_REGEX = /^[0-9a-zA-Z]+\/[0-9a-zA-Z]+$/;

const MAX_FILENAME_LENGTH = 255;

const FILE_NAME_ALLOWED_CHARS  = "\\p{L}0-9_\\-. '";
export const FILE_NAME_CLEANUP = new RegExp(`[^${FILE_NAME_ALLOWED_CHARS}]`, "gu");

export const FILE_NAME_FORMAT = new RegExp(
  `^(?=.{1,${MAX_FILENAME_LENGTH}}$)(?=.*[^ ])(?!.*[. ]$)[${FILE_NAME_ALLOWED_CHARS}]+$`,
  "u"
);

const WINDOWS_UNSAFE_FILENAME_ERROR =
  `sanitizedStringForFilename() produced a filename that is not Windows-safe. ` +
  `Allowed Character Class=${JSON.stringify(FILE_NAME_ALLOWED_CHARS)}`;

export const isWindowsSafeFilename = (name: string): boolean => {
  if (name === "." || name === "..") return false;
  return FILE_NAME_FORMAT.test(name);
};
export const sanitizedStringForFilename = (name: string | null | undefined): string => {
  if (!name) return "";
  const cleaned = name.slice(0, MAX_FILENAME_LENGTH).replace(FILE_NAME_CLEANUP, "").trim().replace(/[. ]+$/gu, "");

  if (cleaned === "") return "";

  assert(isWindowsSafeFilename(cleaned), WINDOWS_UNSAFE_FILENAME_ERROR);

  return cleaned;
};

export type FileInfo = {
  identifier: string,
  name:       string,
  uploadedAt: DateTime
};
// export const globalFileInfo = ({ fileId, name, uploadedAt }: {
//   fileId:     string,
//   name:       string,
//   uploadedAt: DateTime
// }): FileInfo => {
//   assert(fileId      !== undefined);
//   assert(name        !== undefined);
//   assert(uploadedAt  !== undefined);

//   assert(!fileId.startsWith(GLOBAL_FILE_INFO_IDENTIFIER_PREFIX), `Identifier should not start with ${GLOBAL_FILE_INFO_IDENTIFIER_PREFIX}: ${fileId}`);

//   const identifier = `${GLOBAL_FILE_INFO_IDENTIFIER_PREFIX}${fileId}`;

//   if (!GLOBAL_FILE_INFO_IDENTIFIER_REGEX.test(identifier)) {
//     throw new Error(`Invalid identifier: ${identifier}`);
//   }

//   return { identifier, name, uploadedAt };
// }
export const fileInfo = ({ parentId, childId, name, uploadedAt }: {
  parentId:    bigint,
  childId?:    bigint,
  name?:       string,
  uploadedAt?: DateTime,
}) => {
  assert(parentId !== undefined);

  if (childId === undefined) {
    assert(name       === undefined);
    assert(uploadedAt === undefined);
    return undefined;
  }

  assert(name       !== undefined);
  assert(uploadedAt !== undefined);

  const identifier = fileIdentifier({ parentId, childId })

  return { identifier, name, uploadedAt };
}
export const fileInfo_WITH = ({ name }: { name: string }) => (fileInfo: FileInfo): FileInfo => {
  assert(fileInfo !== undefined);
  return { ...fileInfo, name };
}
export function fileIdentifier({ parentId, childId }: { parentId: bigint, childId: bigint }): string {
  if (parentId < 1) throw new Error(`Invalid parentId: ${parentId}`);
  if (childId  < 1) throw new Error(`Invalid childId: ${childId}`);
  return `${encode(parentId)}/${encode(childId)}`;
}
export function fileParentId({ identifier }: { identifier: string }): bigint {
  if (!FILE_INFO_IDENTIFIER_REGEX.test(identifier)) throw new Error(`Invalid identifier: ${identifier}`);
  return decode(identifier.split("/")[0]);
}
export function fileChildId({ identifier }: { identifier: string }): bigint {
  if (!FILE_INFO_IDENTIFIER_REGEX.test(identifier)) throw new Error(`Invalid identifier: ${identifier}`);
  return decode(identifier.split("/")[1]);
}

export function areEqualFileInfos(a?: FileInfo, b?: FileInfo): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a.identifier === b.identifier
      && a.name       === b.name
      && ARE_EQUAL.dateTime(a.uploadedAt, b.uploadedAt);
}

export function isFileInfo(value: any): value is FileInfo {
  assert(value !== undefined);
  return typeof value === "object"
      && "identifier" in value
      && "name"       in value
      && "uploadedAt" in value
      && typeof value.identifier === "string"
      && typeof value.name       === "string"
      && IS.dateTime(value.uploadedAt);
}
